import React, { useState, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import * as Types from 'types'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { withProps } from 'recompose'
import { lpForm } from 'lp-form'
import {
  Field,
  FormSection,
  formValueSelector,
  propTypes as formPropTypes,
} from 'redux-form'
import {
  CheckboxGroup,
  Input,
  InputLabel,
  InputError,
  SubmitButton,
  Textarea,
} from 'lp-components'
import { RequestNameChangeLink } from '../components'
import {
  ProfileMapDisplay,
  SectionBlock,
  SectionHeader,
  CloudinaryFileInput,
  CloudinaryMultiFileInput,
  RemovableThumbnail,
  RemovableFile,
  HiddenLegend,
  ToggleCheckbox,
} from 'components'
import lock from 'images/lock.svg'
import {
  formatObjectsToIds,
  parseIdsToObjects,
  replaceResources,
  convertCloudinarySrcUrlToFilename,
  serializeOptions,
  isNoneType,
  getCloudinaryUploadErrorMessage,
  getNoneTypeId,
  useCommunity,
} from 'utils'
import { camelCase, set, compact, isEmpty, negate, first } from 'lodash'
import { CLOUD_RESIZE_PRESET } from 'config'

const propTypes = {
  ...formPropTypes,
  disciplines: PropTypes.arrayOf(Types.discipline).isRequired,
  leadershipCharacteristics: PropTypes.arrayOf(Types.communityEnumerable)
    .isRequired,
  partnerLocation: Types.location.isRequired,
  flashErrorMessage: PropTypes.func.isRequired,
  flashSuccessMessage: PropTypes.func.isRequired,
  logo: Types.image,
  partnerId: PropTypes.number.isRequired,
  badges: PropTypes.arrayOf(Types.badge),
}

const defaultProps = {
  logo: null,
  badges: [],
}

const NameLabel = (props) => (
  <InputLabel {...props}>
    Organization / Teaching Artist Name{' '}
    <img src={lock} alt="" className="lock" />
  </InputLabel>
)

const WebsiteLabel = (props) => (
  <InputLabel {...props}>
    Website
    <span id="website-instructions" className="input-instructions">
      (full URL e.g., https://www.ingenuity-inc.org/)
    </span>
  </InputLabel>
)

const LogoLabel = ({ image, ...props }) => (
  <InputLabel {...props}>
    Organization Logo / Photo
    <span className="input-instructions">
      (Accepted formats include png, jpg, and jpeg. Your file must be less than
      10 MB and will be cropped to a square when uploaded.)
    </span>
    {!image && (
      <div>
        <span className="badge">
          Personalize your artlook profile by uploading a logo or photo!
        </span>
      </div>
    )}
  </InputLabel>
)

const SpotlightImagesLabel = ({ ...props }) => (
  <InputLabel {...props}>
    Spotlight Images
    <span className="input-instructions">
      (Accepted formats include png, jpg and jpeg. Your file must be less than
      10 MB)
    </span>
  </InputLabel>
)

const SpotlightVideoLabel = ({ ...props }) => (
  <InputLabel {...props}>
    Spotlight Video
    <span className="input-instructions">
      (Accepted formats include mp4. Your file must be less than 500 MB)
    </span>
  </InputLabel>
)

function OrganizationForm({
  disciplines,
  leadershipCharacteristics,
  handleSubmit,
  partnerLocation,
  submitting,
  change,
  flashErrorMessage,
  flashSuccessMessage,
  logo,
  partnerId,
  badges,
  spotlightVideoUrl,
}) {
  const community = useCommunity()
  const { subdomain } = community
  const displayDisciplines = disciplines.filter(negate(isNoneType))
  const [uploadError, setUploadError] = useState()
  const [spotlightVideoError, setSpotlightVideoError] = useState()

  const uploadPath = `/organizations/logos/${partnerId}`
  const spotlightImagesUploadPath = `/organizations/spotlight-images/${partnerId}`
  const spotlightVideoUploadPath = `/organizations/spotlight-video/${partnerId}`

  const showPhotoVideoSection = community?.partnerPhotoVideoEnabled

  const leadershipCharacteristicNoneTypeId = useMemo(
    () => getNoneTypeId(leadershipCharacteristics),
    [leadershipCharacteristics]
  )

  const filterValues = useCallback((values, noneTypeId) => {
    // Remove other values if None type was just selected
    if (first(values) === noneTypeId) {
      // This form currently does not have an other type. If one is created uncomment the code below
      // change('other.otherText', '')
      return [noneTypeId]
    }
    // Otherwsie remove None type
    return values.filter((val) => val !== noneTypeId)
  }, [])

  return (
    <form
      onSubmit={(values) => {
        setUploadError(null)
        setSpotlightVideoError(null)
        handleSubmit(values)
      }}
    >
      <SectionBlock data-cy="profile-details">
        <SectionHeader>
          <h2 id="org-details">Organization Details</h2>
        </SectionHeader>
        <div className="row org-details">
          <fieldset className="outer" aria-labelledby="org-details">
            <div className="col-6">
              <Field
                name="name"
                component={Input}
                labelComponent={NameLabel}
                disabled
                autoComplete="organization"
              >
                <span className="input-link">
                  <RequestNameChangeLink />
                </span>
              </Field>
              <FormSection name="detail">
                <Field
                  name="website"
                  component={Input}
                  labelComponent={WebsiteLabel}
                  aria-describedby="website-instructions"
                  data-cy="website-input"
                  autoComplete="url"
                />
                <Field
                  name="phone"
                  component={Input}
                  data-cy="phone-input"
                  autoComplete="tel"
                />
              </FormSection>
            </div>
            <div className="col-6">
              <FormSection name="logo">
                <Field
                  name="src"
                  accept=".png,.jpg,.jpeg"
                  labelComponent={LogoLabel}
                  image={logo}
                  component={CloudinaryFileInput}
                  uploadPath={uploadPath}
                  uploadPreset={CLOUD_RESIZE_PRESET}
                  onUploadSuccess={({ secureUrl }) => {
                    setUploadError(null)
                    change('logo.src', secureUrl)
                    const newFilename =
                      convertCloudinarySrcUrlToFilename(secureUrl)
                    change('logo.filename', newFilename)
                    // make sure destroy is not true
                    change('logo._destroy', null)
                    flashSuccessMessage(
                      'Organization logo successfully uploaded. Make sure to save your changes.'
                    )
                  }}
                  onUploadFailure={(err) => {
                    const message = getCloudinaryUploadErrorMessage(err)
                    setUploadError(message)
                    flashErrorMessage(message)
                  }}
                  previewComponent={RemovableThumbnail}
                  remove={() => {
                    setUploadError(null)
                    change('logo._destroy', true)
                    flashSuccessMessage(
                      'Existing organization logo successfully removed. Make sure to save your changes.'
                    )
                  }}
                  aria-describedby={uploadError ? 'upload-error' : undefined}
                />
                {uploadError && (
                  <InputError
                    touched
                    invalid
                    error={uploadError}
                    id="upload-error"
                  />
                )}
              </FormSection>
            </div>
          </fieldset>
        </div>
      </SectionBlock>
      {showPhotoVideoSection && (
        <SectionBlock data-cy="photo-video-center">
          <SectionHeader>
            <h2 id="photo-video-center">Photo & Video Center</h2>
          </SectionHeader>
          <div className="row">
            <div className="col-6">
              <Field
                name="spotlightImages"
                component={CloudinaryMultiFileInput}
                labelComponent={SpotlightImagesLabel}
                uploadPath={spotlightImagesUploadPath}
                uploadPreset={CLOUD_RESIZE_PRESET}
                accept=".png,.jpg,.jpeg"
                onUploadSuccess={() =>
                  flashSuccessMessage(
                    'Spotlight image uploaded. Make sure to save your changes.'
                  )
                }
                onUploadFailure={flashErrorMessage}
                captionLabel={(filename) => `Add Image Caption for ${filename}`}
              />
            </div>
            <div className="col-6">
              <Field
                name="spotlightVideoUrl"
                labelComponent={SpotlightVideoLabel}
                accept=".mp4"
                component={CloudinaryFileInput}
                uploadPath={spotlightVideoUploadPath}
                onUploadSuccess={() => {
                  setSpotlightVideoError(null)
                  flashSuccessMessage(
                    'Spotlight video uploaded. Make sure to save your changes.'
                  )
                }}
                onUploadFailure={(err) => {
                  const message = getCloudinaryUploadErrorMessage(err)
                  setSpotlightVideoError(message)
                  flashErrorMessage(message)
                }}
                previewComponent={RemovableFile}
                remove={() => {
                  setSpotlightVideoError(null)
                  change('spotlightVideoUrl', '')
                  change('spotlightVideoCaption', '')
                  change('spotlightVideoMediaAlternative', '')
                  flashSuccessMessage(
                    'Spotlight video removed. Make sure to save your changes.'
                  )
                }}
                aria-describedby={
                  spotlightVideoError ? 'spotlight-video-error' : undefined
                }
              />
              {spotlightVideoError && (
                <InputError
                  touched
                  invalid
                  error={spotlightVideoError}
                  id="spotlight-video-error"
                />
              )}
              {spotlightVideoUrl && (
                <React.Fragment>
                  <Field
                    name="spotlightVideoCaption"
                    component={Input}
                    label="Add Video Caption"
                  />
                  <p
                    className="form-help-text"
                    id="spotlight-video-media-alt-help"
                  >
                    For accessibility (WCAG 2.1), provide a text description of
                    this video: important visuals, on-screen text, scene
                    changes, and anything not fully conveyed by audio or
                    captions. For a simple talking-head video where all meaning
                    is in the audio, you may state that briefly.
                  </p>
                  <Field
                    name="spotlightVideoMediaAlternative"
                    component={Textarea}
                    label="Text description of this video (accessibility)"
                    aria-describedby="spotlight-video-media-alt-help"
                  />
                </React.Fragment>
              )}
            </div>
          </div>
        </SectionBlock>
      )}
      <SectionBlock>
        <SectionHeader>
          <h2 id="about-details">About</h2>
        </SectionHeader>
        <fieldset className="outer" aria-labelledby="about-details">
          <FormSection name="detail">
            <Field
              name="description"
              label="About your organization or teaching artist practice:"
              component={Textarea}
            />
          </FormSection>
        </fieldset>
      </SectionBlock>
      <SectionBlock>
        <SectionHeader>
          <h2 id="location-details">Location</h2>
          <div className="location-block">
            <div className="address-block">
              <fieldset
                className="address-form-block outer"
                aria-labelledby="location-details"
              >
                <FormSection name="address">
                  <Field
                    name="street"
                    component={Input}
                    data-cy="street-input"
                    autoComplete="address-line1"
                  />
                  <div className="row">
                    <Field
                      className="col-5"
                      name="city"
                      component={Input}
                      data-cy="city-input"
                      autoComplete="address-level2"
                    />
                    <Field
                      className="col-3"
                      name="state"
                      component={Input}
                      data-cy="state-input"
                      autoComplete="address-level1"
                    />
                    <Field
                      className="col-4"
                      name="zip"
                      component={Input}
                      data-cy="zip-code-input"
                      autoComplete="postal-code"
                    />
                  </div>
                </FormSection>
              </fieldset>
            </div>
            <div className="map-block">
              <ProfileMapDisplay
                location={partnerLocation}
                zoom={13}
                hidePopup
              />
            </div>
          </div>
        </SectionHeader>
      </SectionBlock>
      <SectionBlock>
        <h2 id="discipline-details">
          Which arts disciplines are addressed by your school programs?
        </h2>
        <p id="discipline-instructions">Please check all that apply</p>
        <div className="response-option-blocks">
          <div className="response-option-block">
            <Field
              aria-labelledby="discipline-details"
              aria-describedby="discipline-instructions"
              name="disciplines"
              labelComponent={HiddenLegend}
              component={CheckboxGroup}
              format={formatObjectsToIds('disciplineId')}
              parse={parseIdsToObjects('disciplineId')}
              options={serializeOptions(displayDisciplines)}
            />
          </div>
        </div>
      </SectionBlock>
      {!isEmpty(leadershipCharacteristics) && (
        <SectionBlock>
          <h2 id="representation-details">
            Which of the following characteristics apply to you or your
            organization?
          </h2>
          <p id="representation-instructions">
            Please select all that apply, if any.{' '}
            {subdomain === 'chicago' &&
              `Do not select an option if it doesn't apply to you. (BIPOC stands for "Black, Indigenous, and/or People of Color".)`}
          </p>
          <Field
            aria-labelledby="representation-details"
            aria-describedby="representation-instructions"
            name="leadershipCharacteristics"
            labelComponent={HiddenLegend}
            component={CheckboxGroup}
            format={formatObjectsToIds('leadershipCharacteristicId')}
            parse={(values) =>
              parseIdsToObjects(
                'leadershipCharacteristicId',
                filterValues(values, leadershipCharacteristicNoneTypeId)
              )
            }
            options={serializeOptions(leadershipCharacteristics)}
          />
        </SectionBlock>
      )}
      {badges.length > 0 && (
        <SectionBlock>
          <SectionHeader>
            <h2 id="badge-details">Badges</h2>
          </SectionHeader>
          <div className="badge-questions-section">
            {badges.map((badge) => (
              <div key={`badge-${badge.id || badge.name}`}>
                <label>{badge.questionText}</label>
                <Field
                  name={camelCase(badge.name)}
                  label={false}
                  component={ToggleCheckbox}
                  className="toggle-checkbox"
                />
              </div>
            ))}
          </div>
        </SectionBlock>
      )}
      <SubmitButton submitting={submitting}>Save</SubmitButton>
    </form>
  )
}

OrganizationForm.propTypes = propTypes
OrganizationForm.defaultProps = defaultProps

const select = formValueSelector('partner-organization')

function mapStateToProps(state) {
  return {
    logo: select(state, 'logo'),
    spotlightVideoUrl: select(state, 'spotlightVideoUrl'),
  }
}

function addTag(resources, tag) {
  return resources.map((resource) => ({ ...resource, tag }))
}

function modifyBeforeSubmit({ initialValues, badges }) {
  const badgeFieldNames = (badges || []).map((b) => camelCase(b.name))

  return {
    beforeSubmit: (values) => {
      const {
        id,
        detail,
        address,
        disciplines,
        leadershipCharacteristics,
        logo,
        spotlightImages,
        spotlightVideoUrl,
        spotlightVideoCaption,
        spotlightVideoMediaAlternative,
      } = values

      const updatedDisciplines = replaceResources({
        old: initialValues.disciplines,
        new: disciplines,
      })
      const updatedLeadershipCharacteristics = replaceResources({
        old: initialValues.leadershipCharacteristics,
        // 'bipoc' tag is added to allow future compatibility in which we capture
        // similar response options for other identity groups (e.g., disability, LGBTQ+)
        new: addTag(
          leadershipCharacteristics,
          Types.LEADERSHIP_CHARACTERISTIC_TAGS.BIPOC
        ),
      })
      const logos = compact([set(logo, 'category', 'standard')])

      const badgeValues = {}
      badgeFieldNames.forEach((name) => {
        if (values[name] !== undefined) {
          badgeValues[name] = values[name]
        }
      })

      return {
        id,
        detail,
        address,
        ...badgeValues,
        disciplines: updatedDisciplines,
        leadershipCharacteristics: updatedLeadershipCharacteristics,
        logos,
        spotlightImages,
        spotlightVideoUrl,
        spotlightVideoCaption,
        spotlightVideoMediaAlternative,
      }
    },
  }
}

export default compose(
  connect(mapStateToProps),
  withProps(modifyBeforeSubmit),
  lpForm({
    name: 'partner-organization',
    constraints: {
      name: { presence: true },
      'detail.website': {
        presence: true,
        format: {
          pattern: /^http(s?):\/\/.*/, // must start with http:// or https:// and can be followed by anything
          flags: 'i',
          message: '^Please provide the full url',
        },
      },
      'detail.phone': {
        presence: true,
        format: {
          pattern: /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/,
          message: 'must be a valid phone number',
        },
      },
      'detail.description': { presence: true, length: { maximum: 1000 } },
      spotlightVideoMediaAlternative: { length: { maximum: 15_000 } },
      'address.street': { presence: true },
      'address.city': { presence: true },
      'address.state': { presence: true },
      'address.zip': { presence: true },
    },
    enableReinitialize: true,
  })
)(OrganizationForm)
